import React from 'react';
import { useLanguage } from '../contexts/LanguageContext';
import { MessageSquare, Search, FileText, Scale, Clock, CheckCircle } from 'lucide-react';

const HowItWorks = () => {
  const { t, direction } = useLanguage();

  const icons = [MessageSquare, Search, FileText, Scale, Clock, CheckCircle];

  return (
    <section id="how-it-works" className="py-24 bg-gradient-to-b from-white to-gray-50 relative overflow-hidden">
      {/* Decorative Elements */}
      <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-transparent via-[#C9A55A]/30 to-transparent" />
      
      <div className="max-w-7xl mx-auto px-6 relative">
        
        {/* Section Header */}
        <div className={`text-center mb-16 ${direction === 'rtl' ? 'rtl' : ''}`}>
          <div className="inline-flex items-center gap-3 px-5 py-2 bg-[#0A1628]/5 border border-[#0A1628]/10 rounded-full mb-6">
            <Clock className="w-4 h-4 text-[#C9A55A]" />
            <span className="text-sm font-semibold text-[#0A1628] uppercase tracking-wider">
              {direction === 'rtl' ? 'كيف نعمل' : 'How It Works'}
            </span>
          </div>
          <h2 className="text-4xl md:text-5xl font-bold text-[#0A1628] mb-4" style={{ fontFamily: 'Georgia, serif' }}>
            {t.howItWorks.title}
          </h2>
          <div className="w-24 h-1 bg-gradient-to-r from-transparent via-[#C9A55A] to-transparent mx-auto mb-4" />
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            {t.howItWorks.subtitle}
          </p>
        </div>

        {/* Steps */}
        <div className="relative">
          {/* Connection Line */}
          <div className="hidden lg:block absolute top-1/2 left-0 right-0 h-1 bg-gradient-to-r from-[#C9A55A]/20 via-[#C9A55A] to-[#C9A55A]/20 -translate-y-1/2 z-0" />
          
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8 relative z-10">
            {t.howItWorks.steps.map((step, idx) => {
              const IconComponent = icons[idx] || CheckCircle;
              return (
                <div 
                  key={idx}
                  className="relative group"
                  data-testid={`step-${idx + 1}`}
                >
                  {/* Card */}
                  <div className="bg-white rounded-2xl shadow-lg hover:shadow-2xl transition-all duration-300 p-8 border border-gray-100 hover:border-[#C9A55A]/30 h-full">
                    
                    {/* Step Number */}
                    <div className="absolute -top-5 left-1/2 -translate-x-1/2">
                      <div className="w-10 h-10 bg-gradient-to-br from-[#C9A55A] to-[#D4AF37] rounded-full flex items-center justify-center text-[#0A1628] font-bold text-lg shadow-lg">
                        {step.number}
                      </div>
                    </div>
                    
                    {/* Icon */}
                    <div className="flex justify-center mt-4 mb-6">
                      <div className="w-16 h-16 bg-gradient-to-br from-[#0A1628] to-[#1a2942] rounded-2xl flex items-center justify-center group-hover:scale-110 transition-transform duration-300">
                        <IconComponent className="w-8 h-8 text-[#C9A55A]" />
                      </div>
                    </div>
                    
                    {/* Content */}
                    <div className={direction === 'rtl' ? 'text-right' : 'text-center'}>
                      <h3 className="text-xl font-bold text-[#0A1628] mb-3">
                        {step.title}
                      </h3>
                      <p className="text-gray-600 leading-relaxed">
                        {step.description}
                      </p>
                    </div>
                    
                    {/* Decorative */}
                    <div className="w-12 h-1 bg-[#C9A55A] mx-auto mt-6 rounded-full group-hover:w-20 transition-all duration-300" />
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* CTA */}
        <div className="text-center mt-16">
          <p className="text-gray-600 mb-6">
            {t.ui.readyToRecover}
          </p>
          <button
            onClick={() => document.getElementById('contact')?.scrollIntoView({ behavior: 'smooth' })}
            className="px-10 py-4 bg-gradient-to-r from-[#0A1628] to-[#1a2942] text-white rounded-lg font-bold text-lg shadow-xl hover:shadow-2xl transition-all duration-300 hover:scale-105 border-2 border-[#C9A55A]/30"
            data-testid="how-it-works-cta"
          >
            {t.hero.cta}
          </button>
        </div>
      </div>
    </section>
  );
};

export default HowItWorks;
